import React, { useState, useEffect } from "react";
import {
	Box,
	Progress,
	Text,
	VStack,
	HStack,
	IconButton,
	Collapse,
	Flex,
	Button,
	useDisclosure,
	Badge,
	Tooltip,
} from "@chakra-ui/react";
import {
	ChevronUpIcon,
	ChevronDownIcon,
	CloseIcon,
	RepeatIcon,
} from "@chakra-ui/icons";
import { CgMinimizeAlt } from "react-icons/cg";
import { FiMaximize2 } from "react-icons/fi";
import { UploadProgress } from "../service/simpleUploadQueue";

interface UploadProgressIndicatorProps {
	isVisible: boolean;
	progress: UploadProgress[];
	onCancel?: (fileId: string) => void;
	onRetry?: (fileId: string) => void;
	onClearCompleted?: () => void;
	onClose?: () => void;
}

export const UploadProgressIndicator: React.FC<
	UploadProgressIndicatorProps
> = ({ isVisible, progress, onCancel, onRetry, onClearCompleted, onClose }) => {
	const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
	const [isMinimized, setIsMinimized] = useState(false);

	const totalFiles = progress.length;
	const completedFiles = progress.filter(
		(p) => p.status === "completed"
	).length;
	const failedFiles = progress.filter((p) => p.status === "failed").length;
	const uploadingFiles = progress.filter(
		(p) => p.status === "uploading"
	).length;
	const pendingFiles = progress.filter((p) => p.status === "pending").length;

	const overallProgress =
		totalFiles > 0 ? progress[0]?.overallProgress || 0 : 0;

	// Auto-minimize after completion
	useEffect(() => {
		if (completedFiles === totalFiles && totalFiles > 0) {
			const timer = setTimeout(() => {
				setIsMinimized(true);
			}, 3000); // Auto-minimize after 3 seconds
			return () => clearTimeout(timer);
		}
	}, [completedFiles, totalFiles]);

	if (!isVisible || totalFiles === 0) {
		return null;
	}

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "green";
			case "failed":
				return "red";
			case "uploading":
				return "blue";
			case "cancelled":
				return "gray";
			default:
				return "gray";
		}
	};

	const getStatusText = (status: string) => {
		switch (status) {
			case "completed":
				return "Completed";
			case "failed":
				return "Failed";
			case "uploading":
				return "Uploading";
			case "pending":
				return "Pending";
			case "cancelled":
				return "Cancelled";
			default:
				return "Unknown";
		}
	};

	return (
		<Box
			position="fixed"
			bottom={4}
			right={4}
			width={isMinimized ? "280px" : "400px"}
			bg="white"
			borderRadius="lg"
			boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
			border="1px solid #e2e8f0"
			zIndex={1000}
			transition="all 0.3s ease"
		>
			{/* Header */}
			<Flex
				align="center"
				justify="space-between"
				p={3}
				bg="#0C64E8"
				color="white"
				borderTopRadius="lg"
				cursor={isMinimized ? "pointer" : "default"}
				onClick={isMinimized ? () => setIsMinimized(false) : undefined}
			>
				<HStack spacing={2}>
					<Text fontSize="sm" fontWeight="semibold">
						Uploading Files
					</Text>
					<Badge colorScheme="white" variant="outline" fontSize="xs">
						{completedFiles}/{totalFiles}
					</Badge>
				</HStack>
				<HStack spacing={1}>
					{!isMinimized && (
						<Tooltip label="Toggle details">
							<span>
								<IconButton
									aria-label="Toggle details"
									icon={isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
									size="sm"
									variant="ghost"
									color="white"
									onClick={onToggle}
									_hover={{ bg: "rgba(255,255,255,0.1)" }}
								/>
							</span>
						</Tooltip>
					)}
					<Tooltip label={isMinimized ? "Expand" : "Minimize"}>
						<IconButton
							aria-label={isMinimized ? "Expand" : "Minimize"}
							icon={isMinimized ? <FiMaximize2 /> : <CgMinimizeAlt />}
							size="sm"
							variant="ghost"
							color="white"
							onClick={() => setIsMinimized(!isMinimized)}
							_hover={{ bg: "rgba(255,255,255,0.1)" }}
						/>
					</Tooltip>
					<IconButton
						aria-label="Close"
						icon={<CloseIcon />}
						size="sm"
						variant="ghost"
						color="white"
						onClick={onClose}
						_hover={{ bg: "rgba(255,255,255,0.1)" }}
					/>
				</HStack>
			</Flex>

			{/* Content */}
			{!isMinimized && (
				<Box p={3}>
					{/* Overall Progress */}
					<VStack spacing={3} align="stretch">
						<Box>
							<HStack justify="space-between" mb={1}>
								<Text fontSize="sm" fontWeight="medium">
									Overall Progress
								</Text>
								<Text fontSize="sm" color="gray.600">
									{overallProgress}%
								</Text>
							</HStack>
							<Progress
								value={overallProgress}
								colorScheme="blue"
								size="sm"
								borderRadius="md"
							/>
						</Box>

						{/* Status Summary */}
						<HStack spacing={4} justify="center">
							{uploadingFiles > 0 && (
								<VStack spacing={1}>
									<Text fontSize="xs" color="blue.500" fontWeight="semibold">
										{uploadingFiles}
									</Text>
									<Text fontSize="xs" color="gray.600">
										Uploading
									</Text>
								</VStack>
							)}
							{pendingFiles > 0 && (
								<VStack spacing={1}>
									<Text fontSize="xs" color="gray" fontWeight="semibold">
										{pendingFiles}
									</Text>
									<Text fontSize="xs" color="gray.600">
										Pending
									</Text>
								</VStack>
							)}
							{completedFiles > 0 && (
								<VStack spacing={1}>
									<Text fontSize="xs" color="green" fontWeight="semibold">
										{completedFiles}
									</Text>
									<Text fontSize="xs" color="gray.600">
										Completed
									</Text>
								</VStack>
							)}
							{failedFiles > 0 && (
								<VStack spacing={1}>
									<Text fontSize="xs" color="red" fontWeight="semibold">
										{failedFiles}
									</Text>
									<Text fontSize="xs" color="gray.600">
										Failed
									</Text>
								</VStack>
							)}
						</HStack>

						{/* Individual File Progress */}
						<Collapse in={isOpen} animateOpacity>
							<VStack
								spacing={2}
								align="stretch"
								maxHeight="200px"
								overflowY="auto"
							>
								{progress.map((file) => (
									<Box
										key={file.fileId}
										p={2}
										bg="gray.50"
										borderRadius="md"
										border="1px solid #e2e8f0"
									>
										<HStack justify="space-between" mb={1}>
											<Text
												fontSize="xs"
												fontWeight="medium"
												noOfLines={1}
												flex={1}
											>
												{file.fileName}
											</Text>
											<Badge
												colorScheme={getStatusColor(file.status)}
												size="sm"
											>
												{getStatusText(file.status)}
											</Badge>
											{file.status === "failed" && onRetry && (
												<IconButton
													aria-label="Retry upload"
													icon={<RepeatIcon />}
													size="xs"
													colorScheme="blue"
													variant="ghost"
													onClick={() => onRetry(file.fileId)}
												/>
											)}
											{(file.status === "uploading" ||
												file.status === "pending") &&
												onCancel && (
													<IconButton
														aria-label="Cancel upload"
														icon={<CloseIcon />}
														size="xs"
														colorScheme="red"
														variant="ghost"
														onClick={() => onCancel(file.fileId)}
													/>
												)}
										</HStack>
										{file.status === "uploading" && (
											<Progress
												value={file.progress}
												colorScheme="blue"
												size="xs"
												borderRadius="sm"
											/>
										)}
									</Box>
								))}
							</VStack>
						</Collapse>

						{/* Action Buttons */}
						{(completedFiles > 0 || failedFiles > 0) && (
							<HStack spacing={2} pt={2}>
								{completedFiles > 0 && onClearCompleted && (
									<Button
										size="sm"
										variant="outline"
										colorScheme="green"
										onClick={onClearCompleted}
										fontSize="xs"
									>
										Clear Completed
									</Button>
								)}
								{failedFiles > 0 && (
									<Text fontSize="xs" color="red.500">
										{failedFiles} file(s) failed to upload
									</Text>
								)}
							</HStack>
						)}
					</VStack>
				</Box>
			)}
		</Box>
	);
};
